import { useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import {
  CurrencyIcon,
  CurrencyInformationSocial,
} from '@root/entities/currency/ui'

import { PairMetricsCell } from './trading-pair-metrics-cell'

import type { PairModel } from '../../atoms'

type PairDetailsProps = {
  pair: PairModel
}

const AVATAR_SIZE = 62

export const PairMetricsMarket: React.FC<PairDetailsProps> = ({ pair }) => {
  const { t } = useTranslation()

  const [price] = useAtom(pair.price)
  const [liquidityUsd] = useAtom(pair.liquidityUsd)
  const [socials] = useAtom(pair.quote.socials)
  const nativeCurrency = CurrencyModel.nativeOf(pair.network)

  const priceUsdState = useMemo(
    () =>
      AsyncStateN.success(
        <Numeric
          value={price.priceUsd}
          preset="currency-amount-short"
          currency="$"
        />,
      ),
    [price.priceUsd],
  )

  const priceNativeState = useMemo(
    () =>
      AsyncStateN.success(
        <Numeric
          value={price.priceNative}
          preset="currency-amount-short"
          currency={nativeCurrency.ticker}
        />,
      ),
    [price.priceNative, nativeCurrency.ticker],
  )

  const supplyState = useMemo(
    () =>
      AsyncStateN.success(
        <Numeric value={pair.quote.supply} preset="currency-amount-short" />,
      ),
    [pair.quote.supply],
  )

  const liquidityState = useMemo(
    () =>
      AsyncStateN.success(
        <Numeric
          value={liquidityUsd}
          preset="currency-amount-short"
          currency="$"
        />,
      ),
    [liquidityUsd],
  )

  return (
    <Root>
      <IconContainer>
        <CurrencyIcon
          withPremiumFallback
          size={AVATAR_SIZE}
          currency={pair.quote}
        />
        <SocialsWrapper>
          <CurrencyInformationSocial socials={socials} />
        </SocialsWrapper>
      </IconContainer>
      <Table>
        <VerticalLine
          css={css`
            grid-column: 1;
            grid-row: 1 / span 3;
          `}
        />
        <VerticalLine
          css={css`
            grid-column: 3;
            grid-row: 1 / span 3;
          `}
        />
        <HorizontalLine />

        <PairMetricsCell
          css={css`
            grid-column: 2;
          `}
          value={priceUsdState}
          title={`${t('common.price')} USD`}
        />
        <PairMetricsCell
          css={css`
            grid-column: 4;
          `}
          value={priceNativeState}
          title={t('common.price')}
        />
        <PairMetricsCell
          css={css`
            grid-column: 2;
            grid-row: 3;
          `}
          value={supplyState}
          title={t('common.supply')}
        />
        <PairMetricsCell
          css={css`
            grid-column: 4;
            grid-row: 3;
          `}
          value={liquidityState}
          title={t('common.liquidity')}
        />
      </Table>
    </Root>
  )
}

const Root = styled.div`
  border-radius: 9px;
  background: var(--background-1);

  display: grid;
  grid-template-columns: auto 1fr;
  min-height: 98px;
`

const IconContainer = styled.div`
  padding-inline: 18px;
  padding-block: 6px;
  display: grid;
  align-items: center;
  gap: 5px;
`

const SocialsWrapper = styled.div`
  display: flex;
  justify-content: center;
`
const Table = styled.div`
  display: grid;
  grid-template-columns: 1px 1fr 1px 1fr;
  grid-template-rows: 1fr 1px 1fr;
`

const VerticalLine = styled.div`
  background: linear-gradient(
    transparent 0%,
    var(--backgroundA-3) 50%,
    transparent 100%
  );
`
const HorizontalLine = styled.div`
  grid-row: 2;
  grid-column: 1 / span 6;
  height: 1px;
  background: var(--backgroundA-3);

  background: linear-gradient(
    90deg,
    var(--backgroundA-3) 50%,
    transparent 100%
  );
`
